import { create } from 'zustand';

import {
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  readStoredAppearance,
  writeStoredAppearance,
  type Appearance,
  type DensityChoice,
  type ThemeChoice,
} from '../lib/appearance';

/**
 * Appearance store — a escolha visual do operador (Eixo 3 / Princípio 6).
 *
 * Guarda a ESCOLHA (`light` | `dark` | `system`), não o tema resolvido: quem
 * escolheu «system» tem de continuar seguindo o SO quando ele virar à noite.
 * A resolução acontece em `hooks/use-appearance.ts`, que ouve o `matchMedia`.
 *
 * **Duas camadas de persistência, com papéis distintos:**
 *   - `localStorage` — síncrono, escrito NA HORA da escolha. É ele que evita o
 *     flash de tela clara no boot e é ele que funciona quando a rede não
 *     responde ou a capability `user-preferences-manage` está negada.
 *   - `UserPreference` namespace `ui` — cross-device, best-effort, escrito pelo
 *     hook. Perder essa escrita degrada a preferência para «só neste navegador»,
 *     nunca para «a tela quebrou».
 *
 * Por isso o store NÃO fala com a rede: mantê-lo síncrono é o que garante que a
 * escolha nunca é perdida por causa de um 403 ou de um servidor fora do ar.
 */
export interface AppearanceState extends Appearance {
  /**
   * Já avisamos nesta sessão que a preferência não chegou ao servidor?
   *
   * Mora aqui, e não numa variável de módulo, por dois motivos: é estado da
   * sessão do operador como qualquer outro, e variável de módulo é invisível
   * para quem for testar o comportamento — vaza de um teste para o seguinte e
   * faz o segundo passar (ou falhar) pelo motivo errado.
   */
  serverPersistWarned: boolean;
  markServerPersistWarned: () => void;
  setTheme: (theme: ThemeChoice) => void;
  setDensity: (density: DensityChoice) => void;
  /**
   * Aplica o que veio do servidor, key a key. Ausência de key significa «este
   * operador nunca escolheu isto» — e ausência NUNCA sobrescreve uma escolha
   * local, senão o boot apagaria em silêncio o que o operador acabou de marcar
   * neste navegador. Valor inválido é tratado como ausência.
   */
  hydrateFromServer: (raw: Record<string, unknown>) => void;
  /** Volta ao default e limpa o cache local (usado no logout). */
  reset: () => void;
}

function persist(next: Appearance): void {
  if (typeof window === 'undefined') return;
  writeStoredAppearance(window.localStorage, next);
}

function initialState(): Appearance {
  if (typeof window === 'undefined') return { ...DEFAULT_APPEARANCE };
  return readStoredAppearance(window.localStorage);
}

const THEMES = new Set<string>(['light', 'dark', 'system']);
const DENSITIES = new Set<string>(['comfortable', 'compact']);

export const useAppearanceStore = create<AppearanceState>((set, get) => ({
  ...initialState(),
  serverPersistWarned: false,

  markServerPersistWarned: () => set({ serverPersistWarned: true }),

  setTheme: (theme) => {
    const next: Appearance = { theme, density: get().density };
    set(next);
    persist(next);
  },

  setDensity: (density) => {
    const next: Appearance = { theme: get().theme, density };
    set(next);
    persist(next);
  },

  hydrateFromServer: (raw) => {
    const current = get();
    const theme = THEMES.has(String(raw.theme)) ? (raw.theme as ThemeChoice) : current.theme;
    const density = DENSITIES.has(String(raw.density))
      ? (raw.density as DensityChoice)
      : current.density;

    if (theme === current.theme && density === current.density) return;

    const next: Appearance = { theme, density };
    set(next);
    persist(next);
  },

  reset: () => {
    set({ ...DEFAULT_APPEARANCE, serverPersistWarned: false });
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(APPEARANCE_STORAGE_KEY);
    } catch {
      /* vide readStoredAppearance */
    }
  },
}));
